/**
 * Token-bound monograph composites shared across companion demos.
 *
 * These components sit one level above the primitive card/KPI pieces:
 * they standardize common panel patterns while still inheriting each
 * demo's local token palette from `createMonographPanelPrimitives`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cva } from "class-variance-authority";
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Info,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./primitives/Tooltip.js";
import type {
  MonographBadgeRailProps,
  MonographCalloutProps,
  MonographCalloutTone,
  MonographFactListProps,
  MonographKpiGridProps,
  MonographPanelGridProps,
  MonographPanelTokens,
  MonographStatusListProps,
  MonographStatusTone,
} from "./MonographPanelPrimitiveTypes.js";

const calloutClass = cva("csk-monograph-callout", {
  variants: {
    tone: {
      danger: "csk-monograph-callout--danger",
      info: "csk-monograph-callout--info",
      neutral: "csk-monograph-callout--neutral",
      safe: "csk-monograph-callout--safe",
      success: "csk-monograph-callout--success",
      warn: "csk-monograph-callout--warn",
    },
  },
  defaultVariants: { tone: "info" },
});

const statusRowClass = cva("csk-monograph-status-row", {
  variants: {
    tone: {
      blocked: "csk-monograph-status-row--blocked",
      complete: "csk-monograph-status-row--complete",
      neutral: "csk-monograph-status-row--neutral",
      planned: "csk-monograph-status-row--planned",
      queued: "csk-monograph-status-row--queued",
      warning: "csk-monograph-status-row--warning",
    },
  },
  defaultVariants: { tone: "neutral" },
});

const CALLOUT_ICON: Readonly<Record<MonographCalloutTone, LucideIcon>> = {
  danger: XCircle,
  info: Info,
  neutral: Info,
  safe: CheckCircle2,
  success: CheckCircle2,
  warn: AlertTriangle,
};

const STATUS_ICON: Readonly<Record<MonographStatusTone, LucideIcon>> = {
  blocked: XCircle,
  complete: CheckCircle2,
  neutral: Info,
  planned: CircleDashed,
  queued: Clock3,
  warning: AlertTriangle,
};

interface CompositeFactoryInput {
  readonly tokens: MonographPanelTokens;
}

function colorForCallout(tokens: MonographPanelTokens, tone: MonographCalloutTone): string {
  if (tone === "safe" || tone === "success")
    return tokens.accentGreen ?? tokens.accentSafe ?? "#22c55e";
  if (tone === "warn") return tokens.accentAmber ?? "#f59e0b";
  if (tone === "danger") return tokens.accentRed ?? tokens.accentForbidden ?? "#ef4444";
  if (tone === "neutral") return tokens.muted;
  return tokens.accentBlue ?? tokens.accent;
}

function colorForStatus(tokens: MonographPanelTokens, tone: MonographStatusTone): string {
  if (tone === "complete") return tokens.accentGreen ?? tokens.accentSafe ?? "#22c55e";
  if (tone === "queued" || tone === "warning") return "#f59e0b";
  if (tone === "blocked") return tokens.accentRed ?? tokens.accentForbidden ?? "#ef4444";
  if (tone === "planned") return "#a78bfa";
  return tokens.muted;
}

function statusTone(status: string | undefined): MonographStatusTone {
  const normalized = status?.toLowerCase() ?? "";
  if (normalized.includes("complete") || normalized.includes("mechanized")) return "complete";
  if (normalized.includes("queued") || normalized.includes("draft")) return "queued";
  if (normalized.includes("planned")) return "planned";
  if (normalized.includes("blocked") || normalized.includes("fail")) return "blocked";
  if (normalized.includes("warn")) return "warning";
  return "neutral";
}

export function createMonographPanelComposites({ tokens }: CompositeFactoryInput): {
  readonly BadgeRail: (props: MonographBadgeRailProps) => React.JSX.Element;
  readonly Callout: (props: MonographCalloutProps) => React.JSX.Element;
  readonly FactList: (props: MonographFactListProps) => React.JSX.Element;
  readonly KpiGrid: (props: MonographKpiGridProps) => React.JSX.Element;
  readonly PanelGrid: (props: MonographPanelGridProps) => React.JSX.Element;
  readonly StatusList: (props: MonographStatusListProps) => React.JSX.Element;
} {
  function PanelGrid({
    children,
    minColumnWidth = 220,
    gap = 10,
    alignItems = "stretch",
    className,
    style,
  }: MonographPanelGridProps): React.JSX.Element {
    return (
      <div
        className={className}
        style={{
          alignItems,
          display: "grid",
          gap,
          gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`,
          ...style,
        }}
      >
        {children}
      </div>
    );
  }

  function KpiGrid({
    children,
    minColumnWidth = 150,
    gap = 10,
  }: MonographKpiGridProps): React.JSX.Element {
    return (
      <PanelGrid minColumnWidth={minColumnWidth} gap={gap}>
        {children}
      </PanelGrid>
    );
  }

  function BadgeRail({
    items,
    ariaLabel = "Badges",
    gap = 6,
    className,
    style,
  }: MonographBadgeRailProps): React.JSX.Element {
    return (
      <TooltipProvider delayDuration={180}>
        <div
          role="list"
          aria-label={ariaLabel}
          className={cn("csk-monograph-badge-rail", className)}
          style={{ display: "flex", flexWrap: "wrap", gap, ...style }}
        >
          {items.map((item, index) => {
            const color = item.color ?? tokens.accent;
            const key = item.id ?? `badge-${String(index)}`;
            const badge = (
              <span
                role="listitem"
                tabIndex={item.title ? 0 : undefined}
                style={{
                  background: colorWithAlpha(color, "18"),
                  border: `1px solid ${colorWithAlpha(color, "55")}`,
                  borderRadius: 999,
                  color,
                  display: "inline-flex",
                  fontFamily: tokens.mono,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0,
                  lineHeight: 1,
                  padding: "5px 8px",
                  whiteSpace: "nowrap",
                }}
              >
                {item.label}
              </span>
            );

            return item.title ? (
              <Tooltip key={key}>
                <TooltipTrigger asChild>{badge}</TooltipTrigger>
                <TooltipContent>{item.title}</TooltipContent>
              </Tooltip>
            ) : (
              <React.Fragment key={key}>{badge}</React.Fragment>
            );
          })}
        </div>
      </TooltipProvider>
    );
  }

  function FactList({
    items,
    ariaLabel = "Fact list",
    dense = false,
    className,
  }: MonographFactListProps): React.JSX.Element {
    const prefersReducedMotion = useReducedMotion() ?? false;

    return (
      <div
        role="list"
        aria-label={ariaLabel}
        className={cn("csk-monograph-fact-list", className)}
        style={{ display: "flex", flexDirection: "column", gap: dense ? 6 : 8 }}
      >
        {items.map((item, index) => {
          const color = item.color ?? tokens.accent;
          const motionProps = prefersReducedMotion
            ? { initial: false as const }
            : {
                animate: { opacity: 1, y: 0 },
                initial: { opacity: 0, y: 4 },
                transition: { delay: index * 0.02, duration: 0.14 },
              };

          return (
            <motion.div
              key={item.id}
              role="listitem"
              {...motionProps}
              style={{
                background: colorWithAlpha(color, "0d"),
                border: `1px solid ${colorWithAlpha(color, "2f")}`,
                borderLeft: `3px solid ${color}`,
                borderRadius: 10,
                padding: dense ? "8px 10px" : "10px 12px",
              }}
            >
              <div style={{ alignItems: "baseline", display: "flex", flexWrap: "wrap", gap: 8 }}>
                <span
                  style={{
                    color: tokens.muted,
                    fontFamily: tokens.mono,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0,
                    textTransform: "uppercase",
                  }}
                >
                  {item.label}
                </span>
                {item.mono ? (
                  <code style={{ color, fontFamily: tokens.mono, fontSize: 12 }}>{item.value}</code>
                ) : (
                  <strong style={{ color: tokens.bright, fontSize: 13 }}>{item.value}</strong>
                )}
              </div>
              {item.detail ? (
                <div style={{ color: tokens.text, fontSize: 12.5, lineHeight: 1.6, marginTop: 4 }}>
                  {item.detail}
                </div>
              ) : null}
            </motion.div>
          );
        })}
      </div>
    );
  }

  function Callout({
    title,
    children,
    tone = "info",
    color = colorForCallout(tokens, tone),
    className,
  }: MonographCalloutProps): React.JSX.Element {
    const prefersReducedMotion = useReducedMotion() ?? false;
    const Icon = CALLOUT_ICON[tone];
    const motionProps = prefersReducedMotion
      ? { initial: false as const }
      : {
          animate: { opacity: 1, y: 0 },
          initial: { opacity: 0, y: 6 },
          transition: { duration: 0.18 },
        };

    return (
      <motion.div
        className={cn(calloutClass({ tone }), className)}
        {...motionProps}
        style={{
          alignItems: "flex-start",
          background: colorWithAlpha(color, "10"),
          border: `1px solid ${colorWithAlpha(color, "44")}`,
          borderLeft: `4px solid ${color}`,
          borderRadius: 12,
          display: "flex",
          gap: 14,
          padding: "14px 16px",
        }}
      >
        <Icon aria-hidden="true" color={color} size={20} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: tokens.bright, fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
            {title}
          </div>
          <div style={{ color: tokens.text, fontSize: 13.5, lineHeight: 1.7 }}>{children}</div>
        </div>
      </motion.div>
    );
  }

  function StatusList({
    items,
    ariaLabel = "Status list",
    dense = false,
    className,
  }: MonographStatusListProps): React.JSX.Element {
    const prefersReducedMotion = useReducedMotion() ?? false;

    return (
      <TooltipProvider delayDuration={180}>
        <div
          role="list"
          aria-label={ariaLabel}
          className={cn("csk-monograph-status-list", className)}
          style={{ display: "flex", flexDirection: "column", gap: dense ? 8 : 10 }}
        >
          {items.map((item, index) => {
            const tone = item.tone ?? statusTone(item.status);
            const color = item.color ?? colorForStatus(tokens, tone);
            const Icon = STATUS_ICON[tone];
            const statusLabel = item.status ?? tone;
            const motionProps = prefersReducedMotion
              ? { initial: false as const }
              : {
                  animate: { opacity: 1, x: 0 },
                  initial: { opacity: 0, x: -6 },
                  transition: { delay: index * 0.025, duration: 0.16 },
                };

            return (
              <motion.div
                key={item.id}
                role="listitem"
                className={statusRowClass({ tone })}
                {...motionProps}
                style={{
                  background: colorWithAlpha(color, "0f"),
                  border: `1px solid ${colorWithAlpha(color, "33")}`,
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 10,
                  padding: dense ? "9px 12px" : "11px 14px",
                }}
              >
                <div style={{ alignItems: "flex-start", display: "flex", gap: 10 }}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        aria-label={statusLabel}
                        tabIndex={0}
                        style={{
                          alignItems: "center",
                          background: colorWithAlpha(color, "18"),
                          border: `1px solid ${colorWithAlpha(color, "44")}`,
                          borderRadius: 8,
                          color,
                          display: "inline-flex",
                          flexShrink: 0,
                          height: 28,
                          justifyContent: "center",
                          width: 28,
                        }}
                      >
                        <Icon aria-hidden="true" size={16} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{statusLabel}</TooltipContent>
                  </Tooltip>
                  <div style={{ minWidth: 0, width: "100%" }}>
                    <div
                      style={{
                        alignItems: "baseline",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      {item.code ? (
                        <code style={{ color, fontFamily: tokens.mono, fontSize: 12 }}>
                          {item.code}
                        </code>
                      ) : null}
                      <strong style={{ color: tokens.bright, fontSize: 13.5 }}>{item.title}</strong>
                      {item.meta ? (
                        <span style={{ color: tokens.muted, fontSize: 11.5, marginLeft: "auto" }}>
                          {item.meta}
                        </span>
                      ) : null}
                    </div>
                    {item.description ? (
                      <div
                        style={{
                          color: tokens.text,
                          fontSize: 12.5,
                          lineHeight: 1.6,
                          marginTop: 5,
                        }}
                      >
                        {item.description}
                      </div>
                    ) : null}
                    {item.path ? (
                      <code
                        style={{
                          color: tokens.muted,
                          display: "block",
                          fontFamily: tokens.mono,
                          fontSize: 11,
                          marginTop: 6,
                        }}
                      >
                        {item.path}
                      </code>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </TooltipProvider>
    );
  }

  return { BadgeRail, Callout, FactList, KpiGrid, PanelGrid, StatusList };
}
